import { useCallback, ReactNode } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import Typography from '@mui/material/Typography';
import { Stack, SxProps } from '@mui/material';
import { useInsightsPageParams } from '@/hooks';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import { Navigation } from '@/components/shared/Navigation/Navigation';
import { NavigationItem } from '@/types';
import { abyssColors } from '@/theme/colors';

type Props = { sx?: SxProps; showTooltipTitle?: boolean; children?: ReactNode; location?: string };

export const AssemblyNavigation = ({ sx, showTooltipTitle, children, location = '' }: Props) => {
  const allAssemblies = useAtomValue(state.allAssembliesForReview);
  const assembliesData =
    allAssemblies?.map((assembly) => ({
      id: assembly.id,
      name: assembly.tagName,
    })) ?? [];

  const [selectedAssemblyId, setSelectedAssemblyId] = useAtom(state.selectedAssemblyId);
  const [selectedAssemblyName, setSelectedAssemblyName] = useAtom(state.selectedAssemblyName);
  const setSelectedPointOfInterestId = useSetAtom(poiState.selectedPointOfInterestId);
  const { setAssembly } = useInsightsPageParams();
  const setCameraTarget = useSetAtom(state.cameraTarget);

  const handleChange = useCallback(
    (assemblyData: Partial<NavigationItem>) => {
      setSelectedAssemblyId(assemblyData.id);
      setCameraTarget(undefined);
      setSelectedAssemblyName(assemblyData.name);
      setAssembly(assemblyData.id ?? '');
      setSelectedPointOfInterestId(undefined);
    },
    [
      setAssembly,
      setCameraTarget,
      setSelectedAssemblyId,
      setSelectedAssemblyName,
      setSelectedPointOfInterestId,
    ]
  );

  return (
    <Stack sx={sx}>
      <Navigation
        handleChange={handleChange}
        selectedItemId={selectedAssemblyId}
        items={assembliesData}
        toolTip={showTooltipTitle ? selectedAssemblyName : ''}
        location={location}
      >
        <Typography
          className="assemblyTitle"
          sx={{
            color: abyssColors.primary[700],
            fontWeight: '700',
          }}
        >
          {selectedAssemblyName}
        </Typography>
      </Navigation>
      {children}
    </Stack>
  );
};
